import {Icon} from 'platform/foundation';

import {without} from 'ramda';

import {useGetUserMenuPinsQuery, useSetUserMenuPinsMutation} from '@dms/api/customer';
import {MenuItemProps, UserSidebarIconSize} from '@dms/api/shared';
import {handleApiError} from '@dms/shared';

import {pushGtmEvent, suffixTestId} from 'shared/web';

import {SIDEBAR_DESKTOP_MENU_ICON_SIZE} from '../constants/sidebarDesktopMenuIconSize';
import {SIDEBAR_MOBILE_ICON_SIZE} from '../constants/sidebarMobileIconSize';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {MenuItemWithPin} from '../utils/addPinsToMenuItems';
import {MenuItemActionButton} from './MenuItemActionButton';

interface MenuItemPinProps
  extends Pick<MenuItemWithPin<MenuItemProps>, 'path' | 'id' | 'isPinned' | 'legacyPinId'> {
  iconSize: UserSidebarIconSize;
}

const SIDEBAR_PINS_MUTATION_KEY = 'sidebar_menu_pins';

export function MenuItemPin(props: MenuItemPinProps) {
  const {data: pinnedMenuItems = []} = useGetUserMenuPinsQuery();
  // Shared across all pin buttons – every request replaces the whole pin list,
  // so only one update may be in flight at a time.
  const [setUserMenuPins, {isLoading: isSaving}] = useSetUserMenuPinsMutation({
    fixedCacheKey: SIDEBAR_PINS_MUTATION_KEY,
  });
  const isMobileNavigation = useIsMobileNavigation();

  const testIdBase = `routePath=${props.path};id=${props.id}-menuItem`;

  // Because of difference in logic from legacy sidebar, we need to do two checks.
  // `props.isPinned` represents the new way of pinning items,
  // while `props.legacyPinId` represents the old way.
  const isPinned = props.isPinned || Boolean(props.legacyPinId);

  // Pins are always saved as the whole ordered list (PUT /web-app-user/pins),
  // the single pin/unpin endpoints no longer exist on the backend.
  const savePins = (pins: string[], eventId: 'sidebar_pin_item' | 'sidebar_unpin_item') =>
    setUserMenuPins({items: pins.map((item) => ({item}))})
      .unwrap()
      .then(() => {
        pushGtmEvent({
          event: 'button_click',
          event_id: eventId,
          value: props.id,
        });
      })
      .catch(handleApiError);

  const handlePin = () => {
    if (isSaving) {
      return;
    }

    if (!isPinned) {
      return savePins([...without([props.id], pinnedMenuItems), props.id], 'sidebar_pin_item');
    }

    // Unpin the item together with its legacy pin (if any)
    const idsToUnpin = props.legacyPinId ? [props.id, props.legacyPinId] : [props.id];

    return savePins(without(idsToUnpin, pinnedMenuItems), 'sidebar_unpin_item');
  };

  return (
    <MenuItemActionButton
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        handlePin();
      }}
      tabIndex={-1}
      disabled={isSaving}
    >
      <Icon
        size={
          isMobileNavigation
            ? SIDEBAR_MOBILE_ICON_SIZE
            : SIDEBAR_DESKTOP_MENU_ICON_SIZE[props.iconSize]
        }
        color="text.white"
        value={isPinned ? 'custom/push_pin' : 'custom/push_pin_outlined'}
        data-testid={suffixTestId('pin', {'data-testid': testIdBase})}
      />
    </MenuItemActionButton>
  );
}
